import {
  Box,
  Checkbox,
  Container,
  FormControlLabel,
  Grid,
  ListItemText,
  MenuItem,
  TextField,
  Typography,
} from "@mui/material";
import { useTranslation } from "common/locales";
import { Formik } from "formik";
import {
  ArableField,
  Field,
  FieldPreparationEvent,
  MACHINERY,
  useArableFields,
  useFields,
} from "model";
import { MutableRefObject, ReactNode } from "react";

import EnterDate from "features/general/EnterDate";
import * as Yup from "yup";
import { FieldPreparationEventFormData } from "./FieldPreparationEventFormData";

const cardSx = {
  backgroundColor: "white",
  borderRadius: "8px",
  border: "1px solid rgba(0, 0, 0, 0.12)",
  p: 2.5,
};

interface Props {
  fieldPreparationEvent: FieldPreparationEvent | undefined;
  handleSave: (
    fieldPreparationEventInput: FieldPreparationEventFormData
  ) => void;
  fieldSelectDisabled: boolean;
  dirtyRef: MutableRefObject<boolean>;
  children: (args: {
    steps: { content: ReactNode }[];
    submitForm: () => Promise<void>;
  }) => ReactNode;
}

const convertToFormInput = (
  fieldPreparationEvent: FieldPreparationEvent
): FieldPreparationEventFormData => {
  return {
    note: fieldPreparationEvent.note ?? undefined,
    machinery: fieldPreparationEvent.machinery ?? undefined,
    fieldPreparationEventDate: new Date(
      fieldPreparationEvent.fieldPreparationEventDate
    ),
    fieldGuids: fieldPreparationEvent.fieldGuids,
    fuel: fieldPreparationEvent.fuel,
    operationalCost: fieldPreparationEvent.operationalCost,
  };
};

export default function EnterFieldPreparationEvent({
  fieldPreparationEvent,
  handleSave,
  fieldSelectDisabled,
  dirtyRef,
  children,
}: Props) {
  const { t } = useTranslation();

  const { data: allGrassFields = [], isLoading: fieldsLoading } = useFields();
  const { data: allArableFields = [], isLoading: arableLoading } =
    useArableFields();
  const loading = fieldsLoading || arableLoading;

  let allFields: (Field | ArableField)[] = [];
  allFields = allFields.concat(allArableFields);
  allFields = allFields.concat(allGrassFields);

  const FieldPreparationEventFormDataSchema = Yup.object({
    note: Yup.string().optional(),
    machinery: Yup.array()
      .of(Yup.mixed<MACHINERY>().oneOf(Object.values(MACHINERY)))
      .min(1)
      .required(),
    fuel: Yup.number().min(0).required(),
    operationalCost: Yup.lazy((value) =>
      value === "" ? Yup.string() : Yup.number().min(0)
    ).optional(),
    fieldGuids: Yup.array()
      .of(Yup.string().required())
      .min(1)
      .required("At least one field guid is required"),
    fieldPreparationEventDate: Yup.date().required(
      "FieldPreparation date is required"
    ),
  });

  if (loading) {
    return null;
  }

  const initialFieldPreparationEvent = fieldPreparationEvent
    ? convertToFormInput(fieldPreparationEvent)
    : {
        note: "",
        fieldPreparationEventDate: new Date(),
        fieldGuids: [],
        machinery: [],
        fuel: "" as unknown as number,
      };
  return (
    <Formik
      enableReinitialize={false}
      initialValues={initialFieldPreparationEvent}
      validationSchema={FieldPreparationEventFormDataSchema}
      validateOnChange={false}
      validateOnBlur={false}
      onSubmit={(
        fieldPreparationEventFormData: FieldPreparationEventFormData
      ) => {
        void handleSave(fieldPreparationEventFormData);
      }}
    >
      {({
        values,
        handleChange,
        errors,
        validateField,
        setFieldValue,
        dirty,
        submitForm,
      }) => {
        dirtyRef.current = dirty;
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const handleInputChange = async (e: any, fieldName: string) => {
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          await (handleChange(e) as any);
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          await (validateField(fieldName) as any);
        };

        const handleFieldToggle = async (fieldGuid: string) => {
          if (fieldSelectDisabled) return;
          const isSelected = values.fieldGuids.includes(fieldGuid);
          const updated = isSelected
            ? values.fieldGuids.filter((g: string) => g !== fieldGuid)
            : [...values.fieldGuids, fieldGuid];
          await setFieldValue("fieldGuids", updated);
        };

        const steps = [
          {
            content: (
              <Container style={{ textAlign: "left" }}>
                <Grid container spacing={2.5}>
                  {/* Date */}
                  <Grid size={12}>
                    <Typography variant="body2" sx={{ mb: 1, fontWeight: 500 }}>
                      {t("fieldPreparation.date.heading")}
                    </Typography>
                    <Box sx={cardSx}>
                      <Grid container spacing={2}>
                        <Grid size={{ xs: 12, sm: 6 }}>
                          <EnterDate
                            disabled={false}
                            date={values.fieldPreparationEventDate}
                            setDate={async (date: Date | null) => {
                              await setFieldValue(
                                "fieldPreparationEventDate",
                                date,
                                false
                              );
                            }}
                            dateError={!!errors.fieldPreparationEventDate}
                            name={"fieldPreparationEventDate"}
                          />
                        </Grid>
                      </Grid>
                    </Box>
                  </Grid>

                  {/* Field selection checkbox list */}
                  <Grid size={12}>
                    <Typography
                      variant="body2"
                      sx={{
                        mb: 1,
                        color: errors.fieldGuids ? "error.main" : undefined,
                        fontWeight: 500,
                      }}
                    >
                      {t("fieldPreparationEventInput.fields.text")}
                    </Typography>
                    <Box
                      sx={{
                        display: "grid",
                        gridTemplateColumns: {
                          xs: "1fr",
                          sm: "1fr 1fr",
                        },
                        borderRadius: "8px",
                        border: "1px solid",
                        borderColor: errors.fieldGuids
                          ? "error.main"
                          : "rgba(0, 0, 0, 0.12)",
                        backgroundColor: "white",
                        maxHeight: 220,
                        overflowY: "auto",
                        py: 0.5,
                        px: 1,
                        opacity: fieldSelectDisabled ? 0.5 : 1,
                      }}
                    >
                      {[...allFields]
                        .sort((a, b) => a.name.localeCompare(b.name))
                        .map((field) => {
                          const isSelected = values.fieldGuids.includes(
                            field.guid
                          );
                          return (
                            <FormControlLabel
                              key={field.guid}
                              control={
                                <Checkbox
                                  checked={isSelected}
                                  onChange={() => handleFieldToggle(field.guid)}
                                  size="small"
                                  sx={{
                                    color: "rgba(0,0,0,0.3)",
                                    "&.Mui-checked": { color: "#7EC254" },
                                  }}
                                />
                              }
                              label={field.name}
                              sx={{
                                mx: 0,
                                "& .MuiFormControlLabel-label": {
                                  fontSize: "0.875rem",
                                  overflow: "hidden",
                                  textOverflow: "ellipsis",
                                  whiteSpace: "nowrap",
                                },
                              }}
                            />
                          );
                        })}
                    </Box>
                  </Grid>

                  {/* Operational details */}
                  <Grid size={12}>
                    <Typography variant="body2" sx={{ mb: 1, fontWeight: 500 }}>
                      {t("fertilizer.operational.cost.heading")}
                    </Typography>
                    <Box sx={cardSx}>
                      <Grid container spacing={2}>
                        <Grid size={{ xs: 12, sm: 6 }}>
                          <TextField
                            required
                            fullWidth
                            value={values.fuel}
                            type="number"
                            id="fuel"
                            error={!!errors.fuel}
                            onChange={async (event) => {
                              await handleInputChange(event, `fuel`);
                            }}
                            label={t("fieldPreparationEvent.fuel.label")}
                            name={`fuel`}
                          />
                        </Grid>
                        <Grid size={{ xs: 12, sm: 6 }}>
                          <TextField
                            select
                            fullWidth
                            required
                            value={values.machinery ?? []}
                            onChange={(e) => handleInputChange(e, "machinery")}
                            error={!!errors.machinery}
                            label={t(
                              "fieldPreparationEventInput.machinery.label"
                            )}
                            name="machinery"
                            SelectProps={{
                              multiple: true,
                              renderValue: (selected) =>
                                (selected as MACHINERY[])
                                  .map((m) =>
                                    t(`yieldEventInput.machinery.options.${m}`)
                                  )
                                  .join(", "),
                            }}
                          >
                            {Object.keys(MACHINERY).map((key) => (
                              <MenuItem key={key} value={key}>
                                <Checkbox
                                  checked={(values.machinery ?? []).includes(
                                    key as MACHINERY
                                  )}
                                  size="small"
                                />
                                <ListItemText
                                  primary={t(
                                    `yieldEventInput.machinery.options.${key}`
                                  )}
                                />
                              </MenuItem>
                            ))}
                          </TextField>
                        </Grid>
                      </Grid>
                    </Box>
                  </Grid>

                  {/* Additional details (optional) */}
                  <Grid size={12}>
                    <Typography variant="body2" sx={{ mb: 1, fontWeight: 500 }}>
                      {t("common.additionalDetails.heading")}
                    </Typography>
                    <Box sx={cardSx}>
                      <Grid container spacing={2}>
                        <Grid size={{ xs: 12, sm: 6 }}>
                          <TextField
                            fullWidth
                            value={values.operationalCost}
                            type="number"
                            id="operationalCost"
                            error={!!errors.operationalCost}
                            onChange={async (event) => {
                              await handleInputChange(event, `operationalCost`);
                            }}
                            label={t("fertilizerEvent.operationalCost.label")}
                            name={`operationalCost`}
                          />
                        </Grid>
                        <Grid size={12}>
                          <TextField
                            id="fieldPreparation-event-input-note"
                            label={t("reseedEvent.notes.label")}
                            value={values.note}
                            helperText={errors.note}
                            error={!!errors.note}
                            fullWidth
                            onChange={(e) => handleInputChange(e, "note")}
                            name={"note"}
                            multiline
                            maxRows={12}
                          />
                        </Grid>
                      </Grid>
                    </Box>
                  </Grid>
                </Grid>
              </Container>
            ),
          },
        ];

        return <>{children({ steps, submitForm })}</>;
      }}
    </Formik>
  );
}
